'use client'

import { motion } from 'framer-motion'
import { Mail, Link as LinkIcon, ExternalLink, Award, Users, Code, Briefcase } from 'lucide-react'
import Link from 'next/link'

export default function TeamPage() {
  const teamMembers = [
    {
      name: 'Rajesh Kumar',
      position: 'CEO & Founder',
      experience: '15+ years',
      expertise: 'Strategic Leadership, Digital Transformation, Business Development',
      image: '👨‍💼',
      bio: 'Visionary leader with extensive experience in scaling technology companies and driving digital transformation initiatives across global markets.',
      achievements: ['Led 200+ successful projects', 'Built teams of 100+ professionals', 'Secured $5M+ in funding'],
      email: 'rajesh@company.com',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Priya Sharma',
      position: 'CTO & Co-Founder', 
      experience: '12+ years',
      expertise: 'Cloud Architecture, AI/ML, Enterprise Solutions, Technical Leadership',
      image: '👩‍💻',
      bio: 'Technical innovator specializing in cloud architecture and emerging technologies, with a track record of delivering scalable solutions.',
      achievements: ['Architect of 50+ cloud solutions', 'AI/ML expert with 10+ patents', 'Speaker at 25+ conferences'],
      email: 'priya@company.com',
      linkedin: '#',
      github: '#'
    },
    {
      name: 'Michael Johnson',
      position: 'VP International Operations',
      experience: '10+ years', 
      expertise: 'Global Business Development, Client Relations, Market Expansion',
      image: '👨‍💼',
      bio: 'International business expert driving global expansion and building strategic partnerships across North America and Europe.',
      achievements: ['Expanded to 15+ countries', 'Built $10M+ revenue pipeline', 'Managed 500+ client relationships'],
      email: 'michael@company.com',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Anita Patel',
      position: 'Head of Delivery',
      experience: '8+ years',
      expertise: 'Project Management, Quality Assurance, Agile Methodologies', 
      image: '👩‍💼',
      bio: 'Delivery excellence champion ensuring on-time, on-budget project completion with the highest quality standards.',
      achievements: ['99% on-time delivery rate', 'Led 300+ project deliveries', 'Certified Agile Master'],
      email: 'anita@company.com',
      linkedin: '#'
    },
    {
      name: 'David Chen',
      position: 'Lead Full Stack Developer',
      experience: '9+ years',
      expertise: 'React, Node.js, Python, DevOps, Microservices Architecture',
      image: '👨‍💻',
      bio: 'Full-stack architect building scalable web applications and leading development teams on complex technical projects.',
      achievements: ['Built 100+ web applications', 'Open source contributor', 'Mentored 50+ developers'],
      email: 'david@company.com',
      github: '#',
      linkedin: '#'
    },
    {
      name: 'Sarah Williams',
      position: 'Head of Design',
      experience: '7+ years',
      expertise: 'UX/UI Design, Design Systems, User Research, Product Strategy',
      image: '👩‍🎨',
      bio: 'Design strategist creating intuitive user experiences and building design systems that scale across product lines.',
      achievements: ['Designed 150+ user interfaces', 'Design System adoption 95%', 'UX research expert'],
      email: 'sarah@company.com',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Alex Rodriguez',
      position: 'DevOps & Cloud Engineer',
      experience: '6+ years',
      expertise: 'AWS, Kubernetes, CI/CD, Infrastructure as Code, Security',
      image: '👨‍🔧',
      bio: 'Cloud infrastructure specialist automating deployments and ensuring high availability across distributed systems.',
      achievements: ['Managed 500+ servers', '99.9% uptime achievement', 'Security compliance expert'],
      email: 'alex@company.com',
      github: '#',
      linkedin: '#'
    },
    {
      name: 'Lisa Thompson',
      position: 'Mobile App Development Lead',
      experience: '8+ years',
      expertise: 'iOS, Android, React Native, Flutter, App Store Optimization',
      image: '👩‍📱',
      bio: 'Mobile development expert creating award-winning apps that have been downloaded millions of times globally.',
      achievements: ['50+ apps published', '10M+ app downloads', 'App Store featured apps'],
      email: 'lisa@company.com',
      github: '#',
      linkedin: '#'
    }
  ]

  const departments = [
    {
      name: 'Engineering',
      count: 25,
      icon: <Code className="w-8 h-8" />,
      description: 'Full-stack developers, mobile developers, DevOps engineers'
    },
    {
      name: 'Design',
      count: 8,
      icon: <Award className="w-8 h-8" />,
      description: 'UX/UI designers, graphic designers, design researchers'
    },
    {
      name: 'Business',
      count: 12,
      icon: <Briefcase className="w-8 h-8" />,
      description: 'Project managers, business analysts, sales team'
    },
    {
      name: 'Operations',
      count: 10,
      icon: <Users className="w-8 h-8" />,
      description: 'HR, finance, administration, quality assurance'
    }
  ]

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Meet Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Amazing Team</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Passionate professionals dedicated to delivering exceptional technology solutions and driving innovation for our clients worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Department Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Departments</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Diverse teams working together to deliver comprehensive technology solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {departments.map((dept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-6 mx-auto">
                  {dept.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{dept.name}</h3>
                <div className="text-3xl font-bold text-blue-600 mb-4">{dept.count}</div>
                <p className="text-gray-600">{dept.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced leaders driving innovation and excellence in technology solutions.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {/* Profile Header */}
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4">{member.image}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-2">{member.position}</p>
                  <p className="text-sm text-gray-500">{member.experience} Experience</p>
                </div>

                {/* Bio */}
                <p className="text-gray-600 mb-6 leading-relaxed">{member.bio}</p>

                {/* Expertise */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Expertise:</h4>
                  <p className="text-sm text-gray-600">{member.expertise}</p>
                </div>

                {/* Key Achievements */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Achievements:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {member.achievements.map((achievement, achieveIndex) => (
                      <li key={achieveIndex} className="flex items-center">
                        <Award className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact Links */}
                <div className="flex space-x-3 pt-4 border-t border-gray-100">
                  <a href={`mailto:${member.email}`} className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
                    <Mail className="w-4 h-4" />
                  </a>
                  {member.linkedin && (
                    <a href={member.linkedin} className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
                      <LinkIcon className="w-4 h-4" />
                    </a>
                  )}
                  {member.twitter && (
                    <a href={member.twitter} className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {member.github && (
                    <a href={member.github} className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Join Our Growing Team</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              We're always looking for talented individuals who are passionate about technology and innovation. 
              Explore career opportunities and be part of our success story.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300">
                View Open Positions
              </Link>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
                Submit Resume
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}